/* Badkamervoorjou: product-only mobile flow. Keeps the real title, forms and search modal. */
(() => {
  'use strict';
  if (customElements.get('bvj-mobile-product-heading')) return;
  let activeOwner = null;

  class BvjMobileProductHeading extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.main = this.closest('product-info[id^="MainProduct"]');
      if (!this.main) return;
      this.media = matchMedia('(max-width: 989px)');
      this.moves = [];
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          if (this.isConnected) this.update();
        });
      };
      const signal = this.controller.signal;
      this.media.addEventListener('change', this.schedule, { signal });
      window.addEventListener('resize', this.schedule, { signal, passive: true });
      window.addEventListener('scroll', this.schedule, { signal, passive: true });
      window.addEventListener('pageshow', this.schedule, { signal });
      document.addEventListener('shopify:section:load', this.schedule, { signal });
      customElements.whenDefined('sticky-header').then(() => {
        if (this.isConnected) this.schedule();
      });
      this.schedule();
    }

    move(node, parent, before = null) {
      if (!node || !parent || this.moves.some(entry => entry.node === node)) return;
      const marker = document.createComment('bvj-mobile-original-position');
      node.before(marker);
      this.moves.push({ node, marker });
      parent.insertBefore(node, before);
    }

    update() {
      if (!this.media.matches) {
        this.restore();
        return;
      }
      const card = this.main.querySelector('.product__info-container');
      if (!card) return;
      if (activeOwner && activeOwner !== this) activeOwner.restore();
      activeOwner = this;
      if (!this.hasAttribute('data-active')) {
        const title = card.querySelector('.product__title');
        if (!title?.querySelector('h1')) return;
        this.move(card.querySelector('.product__text--brand'), this);
        this.move(title, this);
        this.move(card.querySelector('.bvj-product-identifiers'), this);
        // The full description stays below the purchase area. Keep its shortcut, not a duplicate introduction.
        const intro = card.querySelector('.bvj-product-intro');
        const support = card.querySelector('.bvj-purchase-support');
        this.move(intro, card, support || null);
        this.setAttribute('data-active', '');
      }
      document.documentElement.classList.add('bvj-mobile-product-flow');
      this.setupHeader();
      this.updateHeader();
    }

    setupHeader() {
      const section = document.querySelector('.section-header');
      const sticky = section?.querySelector('sticky-header');
      if (!section || !sticky || typeof sticky.headerIsAlwaysSticky !== 'boolean') return;
      if (this.headerState?.section === section && this.headerState.sticky === sticky) return;
      this.restoreHeader();
      const header = section.querySelector('header.header');
      const search = header?.querySelector('.header__icons > .header__search');
      if (!header || !search) return;
      const marker = document.createComment('bvj-search-original-position');
      search.before(marker);
      this.headerState = {
        section, sticky, search, marker,
        originalAlways: sticky.headerIsAlwaysSticky,
        originalStickyClass: section.classList.contains('shopify-section-header-sticky')
      };
      header.append(search);
      sticky.headerIsAlwaysSticky = true;
      section.classList.add('bvj-product-fixed-header', 'shopify-section-header-sticky');
      section.classList.remove('shopify-section-header-hidden');
      this.headerResize = new ResizeObserver(() => this.measureHeader());
      this.headerResize.observe(section);
    }

    updateHeader() {
      if (!this.headerState) return;
      const section = this.headerState.section;
      const y = Math.max(0, window.scrollY);
      const compact = section.classList.contains('bvj-product-header-compact');
      // Hysteresis avoids flickering or scroll loops when the search row collapses.
      if (!compact && y > 160) section.classList.add('bvj-product-header-compact');
      else if (compact && y < 24) section.classList.remove('bvj-product-header-compact');
      section.classList.remove('shopify-section-header-hidden');
      this.measureHeader();
    }

    measureHeader() {
      if (!this.headerState?.section.isConnected) return;
      const height = Math.ceil(this.headerState.section.getBoundingClientRect().height);
      const root = document.documentElement;
      const value = `${height}px`;
      if (root.style.getPropertyValue('--bvj-product-header-height') !== value) {
        root.style.setProperty('--bvj-product-header-height', value);
        this.headerState.sticky.setHeaderHeight?.();
      }
    }

    restoreHeader() {
      this.headerResize?.disconnect();
      this.headerResize = null;
      const state = this.headerState;
      if (!state) return;
      if (state.marker.parentNode) state.marker.replaceWith(state.search);
      state.sticky.headerIsAlwaysSticky = state.originalAlways;
      state.section.classList.remove('bvj-product-fixed-header', 'bvj-product-header-compact');
      state.section.classList.toggle('shopify-section-header-sticky', state.originalStickyClass);
      state.sticky.setHeaderHeight?.();
      this.headerState = null;
      document.documentElement.style.removeProperty('--bvj-product-header-height');
    }

    restore() {
      this.restoreHeader();
      this.moves.forEach(({ node, marker }) => {
        if (marker.parentNode) marker.replaceWith(node);
      });
      this.moves = [];
      this.removeAttribute('data-active');
      if (activeOwner === this) {
        document.documentElement.classList.remove('bvj-mobile-product-flow');
        activeOwner = null;
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.restore();
    }
  }
  customElements.define('bvj-mobile-product-heading', BvjMobileProductHeading);
})();
